import { Component, ViewEncapsulation, OnInit, HostListener, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
    Router,
    NavigationEnd,
    ActivatedRoute,
    NavigationStart,
    ActivationEnd
} from '@angular/router';
import { AppSettings } from './app.settings';
import { Settings } from './models/settings.model';
import { AuthService } from './services/auth.service';

import { filter } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import { ScrollHelper } from './shared/scrollHelper';
import { GoogleAnalytics } from './services/google-analytics.service';
import {PaymentService} from './services/payments.service';
import {ModalService} from './services/modal.service';
import {AlertType} from './enums/alert-type';
import {MobileCheckServiceService} from './services/mobile-check-service.service';
import {ScriptService} from './services/script.service';
import {OneTrustService} from './services/one-trust.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})

export class AppComponent implements OnInit, OnDestroy {

    public settings: Settings;
    public showMenu = false;
    public menus = ['vertical', 'horizontal'];
    public menuOption: string;
    public menuTypes = ['default', 'compact', 'mini'];
    public menuTypeOption: string;
    public headerVisible = true;

    private alive = true;
    private modalObserver: MutationObserver;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Configuration: Modals that should be disabled when other modals stack on top
     * Add additional selectors here to extend the stacking behavior to other modals
     */
    private readonly stackableModalSelectors = [
        '.manage-location--modal',  // Manage Locations modal
        '.sap-modal',
        '.order-settings',
        // Add more selectors here as needed in the future
        // Example: '.checkout--modal',
    ];



    private scrollHelper: ScrollHelper;


    constructor(
        public appSettings: AppSettings,
        protected authService: AuthService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private viewportScroller: ViewportScroller,
        private googleAnalytics: GoogleAnalytics,
        private paymentService: PaymentService,
        private modalService: ModalService,
        private ngModal: NgbModal,
        private mobileCheck: MobileCheckServiceService,
        private scriptService: ScriptService,
        private oneTrustService: OneTrustService

    ) {
        this.settings = this.appSettings.settings;
    }

    ngOnInit() {
        this.oneTrustService.init();
        this.scriptService.loadScripts();
        this.scrollHelper = new ScrollHelper(this.viewportScroller);

        if (this.mobileCheck.mobileDevice || this.mobileCheck.tabletDevice) {
            this.settings.theme.showMenu = false;
            this.settings.theme.sideChatIsHoverable = false;
        }
        this.showMenu = this.settings.theme.showMenu;
        this.menuOption = this.settings.theme.menu;
        this.menuTypeOption = this.settings.theme.menuType;

        // this.router.events.subscribe((evt) => {
        //     if (!(evt instanceof NavigationEnd)) {
        //         return;
        //     }
        //     window.scrollTo(0, 0)
        // });
        /*Added to refresh the route when on same page. */
        this.router.routeReuseStrategy.shouldReuseRoute = function () {
            return false;
        };

        // this.router.events.subscribe((evt) => {
        //     if (evt instanceof NavigationEnd) {
        //         this.router.navigated = false;
        //         window.scrollTo(0, 0);
        //     }
        // });
        /* */
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
        ).subscribe((e: NavigationEnd) => {
            this.scrollHelper.handleScroll(e.urlAfterRedirects);

            // show warning dialog in case of failed transaction
            this.showFailedTransaction(e.url);
        });


        this.router.events.pipe(
                filter(event => {
                    return event instanceof ActivationEnd && !!event.snapshot.component
                    // return event instanceof ActivationEnd
                }),
            )
            .subscribe((event: ActivationEnd) => {
                const title = event['snapshot'].data['title'];
                if (title) {
                    this.googleAnalytics.navigationTo(this.router.routerState.snapshot.url, title);
                }
            });


        this.router.events.pipe(
            filter(e => e instanceof NavigationStart)
        ).subscribe((e: NavigationStart) => {
            // starting navigation event;
            // get current scroll offset
            this.scrollHelper.storeScroll(this.router.routerState.snapshot.url);
            if (e.url === '/not-supported' || (e.url === '/login' && !this.authService.user)) {
                // not supported page has it's own header or not need it
                this.headerVisible = false;
            } else {
                this.headerVisible = !!this.authService.user;
            }


            if (this.ngModal.hasOpenModals()) {
                this.ngModal.dismissAll();
            }

        });

        // Setup modal stacking observer to prevent interaction with underlying modals
        this.setupModalStackObserver();
    }

    /**
     * Sets up a MutationObserver to watch for modal additions/removals
     * and automatically disable configured modals when other modals stack on top
     * @see stackableModalSelectors for configuration
     */
    private setupModalStackObserver(): void {
        // Only run in browser environment
        if (typeof document === 'undefined') {
            return;
        }

        this.modalObserver = new MutationObserver((mutations) => {
            // Check if any mutations actually involved modal elements
            const hasModalChanges = mutations.some(mutation => {
                const addedModals = Array.from(mutation.addedNodes).some(
                    node => node instanceof Element && node.classList.contains('modal')
                );
                const removedModals = Array.from(mutation.removedNodes).some(
                    node => node instanceof Element && node.classList.contains('modal')
                );
                return addedModals || removedModals;
            });

            // Only update if modal elements were actually added/removed
            if (hasModalChanges) {
                // Debounce to avoid multiple rapid updates
                clearTimeout(this.debounceTimer);
                this.debounceTimer = setTimeout(() => this.updateModalStack(), 50);
            }
        });

        // Observe only direct children of body (modals are appended here)
        this.modalObserver.observe(document.body, {
            childList: true,
            subtree: false // Only watch direct children, not entire subtree
        });
    }

    /**
     * Updates the modal stack by disabling configured modals when other modals appear on top
     * Iterates through all configured selectors and applies stacking behavior
     * @see stackableModalSelectors for which modals are managed
     */
    private updateModalStack(): void {
        const allModals = Array.from(document.querySelectorAll('.modal'));
        
        if (allModals.length === 0) {
            return;
        }

        // Process each configured modal selector
        this.stackableModalSelectors.forEach(selector => {
            // Find the modal matching this selector
            const targetModal = allModals.find(modal => 
                modal.querySelector(selector) !== null
            );

            if (!targetModal) {
                return; // This modal is not currently open
            }

            // Check if there are other modals after this target modal
            const targetModalIndex = allModals.indexOf(targetModal);
            const hasModalsOnTop = targetModalIndex < allModals.length - 1;

            // Disable target modal only if other modals are stacked on top of it
            if (hasModalsOnTop) {
                targetModal.classList.add('modal-stack-disabled');
            } else {
                targetModal.classList.remove('modal-stack-disabled');
            }
        });
    }



    @HostListener('window:resize')
    public onWindowResize(): void {
        const showMenu = (window.innerWidth > 768);

        if (this.showMenu !== showMenu) {
            this.settings.theme.showMenu = showMenu;
        }
        this.showMenu = showMenu;
    }

    ngOnDestroy() {
        this.alive = false;
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        if (this.modalObserver) {
            this.modalObserver.disconnect();
        }
    }

    private getTitle() {
        let child = this.activatedRoute.firstChild;
        while (child) {
            if (child.firstChild) {
                child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data) {
                return child.snapshot.data;
            } else {
                return '';
            }
        }
        return '';
    }


    private showFailedTransaction(navigationUrl = '') {
        if (navigationUrl.startsWith('/account/transactions')) {
            return false;
        }

        if (this.paymentService.hasFailedTransactions) {
            const alertSettings = {
                type: AlertType.Confirm,
                class: 'modal-sm',
                headerClass: 'state modal-danger',
                headerIcon: 'fa-warning',
                title: 'Failed Transactions',
                message: 'You have failed transactions, you must update your credit card now to proceed.',
                actionLabel: 'Show'
            };
            this.modalService.showAlert(alertSettings).then( dialogResult => {
                if (dialogResult === true) {
                    this.router.navigate(['/account/transactions'], {queryParams: {showFailed: true}});
                }
            });

        }
    }



    get mobile(): boolean {
        return this.mobileCheck.mobile;
    }

}
